<script setup lang="ts">
export interface BaseButtonProps {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost'
  size?: 'sm' | 'md' | 'lg'
  type?: 'button' | 'submit' | 'reset'
  disabled?: boolean
  loading?: boolean
  fullWidth?: boolean
}

const props = withDefaults(defineProps<BaseButtonProps>(), {
  variant: 'primary',
  size: 'md',
  type: 'button',
  disabled: false,
  loading: false,
  fullWidth: false,
})

const emit = defineEmits<{
  click: [event: MouseEvent]
}>()

const handleClick = (event: MouseEvent) => {
  if (!props.disabled && !props.loading) {
    emit('click', event)
  }
}

// Size-based classes
const sizeClasses = {
  sm: 'px-3 py-1.5 text-xs',
  md: 'px-4 py-2 text-sm',
  lg: 'px-6 py-3 text-base',
}

// Variant-based classes. Colors come from semantic tokens (primary = brand emerald,
// danger = red) defined in tailwind.config.js — no raw palette names here.
const variantClasses = {
  primary: [
    'bg-primary-600',
    'border-transparent',
    'text-white',
    'hover:bg-primary-700',
    'focus:ring-primary-500',
  ],
  secondary: [
    'bg-white',
    'border-gray-300',
    'text-gray-700',
    'hover:bg-gray-50',
    'focus:ring-primary-500',
  ],
  danger: [
    'bg-white',
    'border-danger-300',
    'text-danger-700',
    'hover:bg-danger-50',
    'focus:ring-danger-500',
  ],
  ghost: [
    'bg-transparent',
    'border-transparent',
    'text-gray-700',
    'hover:bg-gray-100',
    'focus:ring-primary-500',
  ],
}
</script>

<template>
  <button
    :type="type"
    :disabled="disabled || loading"
    :class="[
      // Base styling
      'inline-flex',
      'items-center',
      'justify-center',
      'border',
      'rounded-md',
      'font-medium',
      'transition-colors',
      'duration-200',
      // Size classes
      sizeClasses[size],
      // Variant classes
      variantClasses[variant],
      // Width
      fullWidth ? 'w-full' : 'flex-1',
      // Interactive states (only when not disabled/loading)
      !(disabled || loading) && [
        'hover:cursor-pointer',
        'focus:outline-none',
        'focus:ring-2',
        'focus:ring-offset-2',
      ],
      // Disabled/loading states
      (disabled || loading) && ['opacity-50', 'cursor-not-allowed'],
    ]"
    @click="handleClick"
  >
    <!-- Loading spinner -->
    <svg v-if="loading" class="w-4 h-4 mr-2 animate-spin" fill="none" viewBox="0 0 24 24">
      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
      <path
        class="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 0 1 4 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>

    <!-- Button content -->
    <slot />
  </button>
</template>
